import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';

export function EndBlockDocsList({
  docIds,
  docMeta,
  selectedDocIds,
  setSelectedDocIds,
  onDocClick,
  actionsMenu,
}: {
  docIds: string[];
  docMeta: Map<string, { name: string; type: string | null }>;
  selectedDocIds: Set<string>;
  setSelectedDocIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  onDocClick?: (docId: string) => void;
  actionsMenu: React.ReactNode;
}) {
  const allRowsChecked = docIds.length > 0 && selectedDocIds.size === docIds.length;

  const toggleAllHandler = () => {
    if (allRowsChecked) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(docIds));
    }
  };

  const handleSelect = (docId: string, checked: boolean) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  return (
    <>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 gap-y-4 pr-3.5 pl-5.25">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Checkbox checked={allRowsChecked} onCheckedChange={toggleAllHandler} className="mr-2" />
          <p>
            <strong>{selectedDocIds.size.toLocaleString()}</strong> Documents selected
          </p>

          <Button
            size="sm"
            variant="ghost"
            disabled={selectedDocIds.size === 0}
            onClick={() => setSelectedDocIds(new Set())}
            className="gap-1"
          >
            <Icon className="lucide--x" />
            Clear all
          </Button>
        </div>
        {actionsMenu}
      </div>

      <div className="mt-2.5 flex flex-col gap-2 px-1">
        {docIds.map((docId) => {
          const meta = docMeta.get(docId);
          const isSelected = selectedDocIds.has(docId);
          return (
            <div
              key={docId}
              className="group flex min-w-0 flex-1 items-center gap-3 rounded-xl border bg-white px-4 py-3 text-[13px] transition-colors duration-150 hover:border-gray-300"
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={(v) => handleSelect(docId, !!v)}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                onClick={() => onDocClick?.(docId)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <span className="line-clamp-1 min-w-0 flex-1 text-gray-700 hover:text-blue-600">
                  {meta?.name ?? docId}
                </span>
                {meta?.type && <Chip className={CHIP_COLOURS.type}>{meta.type}</Chip>}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function EndBlockDocsActionsMenu({
  onCreateDocChronology,
  onAddToExistingDocChronology,
  onCreateSubset,
  onAddToExistingSubset,
  onSelectOnPage,
  onDownloadZip,
  onDownloadBundle,
  disabled,
  isCreatingDocChronology = false,
}: {
  onCreateDocChronology: () => void;
  onAddToExistingDocChronology: () => void;
  onCreateSubset: () => void;
  onAddToExistingSubset: () => void;
  onSelectOnPage: () => void;
  onDownloadZip: () => void;
  onDownloadBundle: () => void;
  disabled: boolean;
  isCreatingDocChronology?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <DropdownMenu size="sm">
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="white" disabled={disabled || isCreatingDocChronology}>
            {isCreatingDocChronology ? (
              <Icon className="lucide--loader animate-spin ease-in-out" />
            ) : (
              <Icon className="lucide--folder-git-2" />
            )}
            Document Chronology
            <Icon className="lucide--chevron-down" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem onClick={onCreateDocChronology}>
            <Icon className="lucide--plus" />
            Create new from selected documents
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onAddToExistingDocChronology}>
            <Icon className="lucide--git-branch-plus" />
            Add selected documents to existing
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu size="sm">
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="white" disabled={disabled}>
            <Icon className="lucide--group" />
            Subset
            <Icon className="lucide--chevron-down" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem onClick={onCreateSubset}>
            <Icon className="lucide--plus" />
            Create new from selected documents
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onAddToExistingSubset}>
            <Icon className="lucide--list-plus" />
            Add selected documents to existing
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu size="sm">
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="my-0.75" disabled={disabled}>
            <Icon className="lucide--more-horizontal" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem onClick={onSelectOnPage}>
            <Icon className="lucide--list-checks" />
            Select documents on docs page
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon className="lucide--download" />
              Export selected documents
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={onDownloadZip}>
                <Icon className="lucide--file-archive" />
                Zip
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownloadBundle}>
                <Icon className="lucide--book" />
                PDF Bundle
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
